/* ─────────────────────────────────────────────────────────────────────
   HE · entry attribution · 2026-09-25 · place-first PDP §B

   WHAT IT ANSWERS
   "Which proposition brought this order in?" Shopify's own reports can say
   which campaign a session came from, but not which *place* was advertised
   once the buyer switches format mid-visit — and switching format is the
   whole point of the place-first PDP. So the landing's utm_content is kept
   for the visit and written onto the cart, where it lands on the order and
   can be read per order in the admin.

   THREE JOBS
   1. Carry the query string across a format switch. The selector links go to
      a different product URL, which would otherwise drop utm_* and fbclid.
   2. Remember the first touch. utm_content becomes entry_place and
      utm_campaign becomes entry_campaign, in sessionStorage with a 7-day
      localStorage copy. First touch wins: a later ad click inside the same
      session does not overwrite it.
   3. Put both on the cart as attributes[entry_place] / [entry_campaign], so
      they appear on the order. Written after a confirmed add — once per
      cart, never on a page view, so a visitor who never adds leaves no cart
      behind.

   WHY A FETCH HOOK
   The theme adds to the cart from three places (buy panel, sticky bar, size
   sheet) and, without JS, by native form POST. Rather than edit each path,
   this watches for a completed POST to /cart/add.js and reacts to it, and
   also fires a beacon on a native product-form submit. Both are idempotent:
   the attributes are written once per cart token.
   ───────────────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  var KEY_PLACE = 'he_entry_place';
  var KEY_CAMPAIGN = 'he_entry_campaign';
  var KEY_WRITTEN = 'he_entry_written';
  var TTL_DAYS = 7;
  var CARRY = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id', 'fbclid'];

  /* ── storage · sessionStorage first, localStorage as the 7-day backup ── */
  function readSession(key) {
    try { return window.sessionStorage.getItem(key); } catch (e) { return null; }
  }
  function writeSession(key, value) {
    try { window.sessionStorage.setItem(key, value); } catch (e) {}
  }
  function readLocal(key) {
    try {
      var raw = window.localStorage.getItem(key);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || !parsed.v) return null;
      if (parsed.exp && Date.now() > parsed.exp) {
        window.localStorage.removeItem(key);
        return null;
      }
      return parsed.v;
    } catch (e) { return null; }
  }
  function writeLocal(key, value) {
    try {
      window.localStorage.setItem(key, JSON.stringify({
        v: value, exp: Date.now() + TTL_DAYS * 86400000
      }));
    } catch (e) {}
  }
  function remembered(key) {
    return readSession(key) || readLocal(key);
  }
  function remember(key, value) {
    if (!value) return;
    // first touch wins, for the whole session and for the 7 days after it
    if (remembered(key)) return;
    writeSession(key, value);
    writeLocal(key, value);
  }

  /* ── 2 · capture the landing ──────────────────────────────────────── */
  var params = new URLSearchParams(window.location.search);
  remember(KEY_PLACE, (params.get('utm_content') || '').trim());
  remember(KEY_CAMPAIGN, (params.get('utm_campaign') || '').trim());

  /* ── 1 · carry the query string onto the format links ─────────────── */
  function carryQueryString() {
    var carried = [];
    CARRY.forEach(function (name) {
      var value = params.get(name);
      if (value) carried.push([name, value]);
    });
    if (!carried.length) return;

    var links = document.querySelectorAll('[data-edition-link]');
    Array.prototype.forEach.call(links, function (a) {
      var href = a.getAttribute('href');
      if (!href || href.charAt(0) === '#') return;
      var url;
      try { url = new URL(href, window.location.origin); } catch (e) { return; }
      carried.forEach(function (pair) {
        // never clobber a parameter the link already carries on purpose
        if (!url.searchParams.has(pair[0])) url.searchParams.set(pair[0], pair[1]);
      });
      a.setAttribute('href', url.pathname + url.search + url.hash);
    });
  }

  /* ── 3 · write the attributes onto the cart ───────────────────────── */
  function attributesToWrite() {
    var place = remembered(KEY_PLACE);
    var campaign = remembered(KEY_CAMPAIGN);
    if (!place && !campaign) return null;
    var attrs = {};
    if (place) attrs.entry_place = place;
    if (campaign) attrs.entry_campaign = campaign;
    return attrs;
  }

  function alreadyWritten() {
    return readSession(KEY_WRITTEN) === '1';
  }

  function writeAttributes() {
    var attrs = attributesToWrite();
    if (!attrs || alreadyWritten()) return;
    writeSession(KEY_WRITTEN, '1');
    fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    }).then(function (r) {
      if (!r.ok) {
        // let a later add try again rather than losing the attribution
        writeSession(KEY_WRITTEN, '0');
        console.warn('[he-attribution] /cart/update.js', r.status);
      }
    }).catch(function (err) {
      writeSession(KEY_WRITTEN, '0');
      console.warn('[he-attribution] network', err);
    });
  }

  // A native form POST navigates away, so the write has to survive the
  // unload: sendBeacon does, a fetch does not reliably.
  function beaconAttributes() {
    var attrs = attributesToWrite();
    if (!attrs || alreadyWritten()) return;
    if (!navigator.sendBeacon) { writeAttributes(); return; }
    try {
      var blob = new Blob([JSON.stringify({ attributes: attrs })], { type: 'application/json' });
      if (navigator.sendBeacon('/cart/update.js', blob)) writeSession(KEY_WRITTEN, '1');
    } catch (e) {
      writeAttributes();
    }
  }

  /* Watch for a completed add. Pass-through wrapper: the response is
     returned untouched and an exception here can never break an add. */
  if (window.fetch && !window.__heAttributionHooked) {
    window.__heAttributionHooked = true;
    var nativeFetch = window.fetch;
    window.fetch = function (input, init) {
      var url = '';
      try { url = String((input && input.url) ? input.url : input); } catch (e) {}
      var result = nativeFetch.apply(this, arguments);
      if (url.indexOf('/cart/add') > -1) {
        result.then(function (r) {
          try { if (r && r.ok) writeAttributes(); } catch (e) {}
        }).catch(function () {});
      }
      return result;
    };
  }

  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  ready(function () {
    carryQueryString();
    // native (no-JS-path) submits of the product form
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || !form.getAttribute) return;
      var action = form.getAttribute('action') || '';
      if (action.indexOf('/cart/add') > -1) beaconAttributes();
    }, true);
  });

  // read-only handle, for QA from the console
  window.HEAttribution = {
    place: function () { return remembered(KEY_PLACE); },
    campaign: function () { return remembered(KEY_CAMPAIGN); }
  };
})();
